import { createAction, handleActions } from 'redux-actions';
import { createSelector } from 'reselect';
import { replace } from 'react-router-redux';

import { useRedux } from 'util/hook/redux';

import { wrapFetch, wrapAuthFetch } from 'util/api';
import { sendAddToWishlist } from 'util/analytics';
import { openNormalAlert, openWarningAlert } from './alert';

export const defaultProductData = {
	id: 1,
	name: '',
	description: '',
	price: 0,
	discount: 0,
	discount_text: '',
	feature_cover:
		'https://member-yuanyu.25demo.com/backstage/upload/productPage/20190221-68490d4038d491f9a998c8a10d3b844a.jpg',
	cover:
		'https://member-yuanyu.25demo.com/backstage/upload/productPage/20190221-0a404aa9b773796f89ece1b79312497a.jpg',
	categories: [{ id: -1, name: '' }],
	bread_crumb: [],
	features: [],
	stocks: [],
	specs: [],
	deliveries: [],
	items: [],
	recommends: [],
	increase_purchases: [],
	content: '',
};

const checkUpdateProductData = (originProductGroup, data) => {
	const productId = data.id;

	if (Object.prototype.hasOwnProperty.call(originProductGroup, productId)) {
		return {
			...originProductGroup[productId],
			...data,
		};
	}

	return { ...defaultProductData, ...data };
};

const normalizeProduct = (originProductGroup, data) => {
	const ProductData = {};
	const list = data.map(item => {
		ProductData[item.id] = checkUpdateProductData(originProductGroup, item);

		return item.id;
	});

	return { data: ProductData, list };
};

export const getProductList = createAction(
	'GET_PRODUCT_LIST',
	(cateid, filterId, page = 1, sortBy = 'price', sort = 'desc', append = false) => async (
		_,
		getState,
	) => {
		const { status, data } = await wrapFetch(
			'product',
			{ method: 'GET' },
			{
				sortBy,
				sort,
				page,
				take: 8,
				category_id: cateid,
				filter_id: filterId,
			},
		);

		if (status !== 200) {
			return { data: {}, list: [] };
		}

		const {
			product: {
				data: originProductGroup,
				products: { list: originProductList },
			},
		} = getState();

		const { list, data: productData } = normalizeProduct(originProductGroup, data.data);

		return {
			data: append ? { ...originProductGroup, ...productData } : productData,
			list: append ? [...originProductList, ...list] : list,
			currentPage: data.current_page,
			totalPage: data.total_page,
			total: data.total,
			perPage: data.per_page,
		};
	},
);

export const getProductSearch = createAction(
	'GET_PRODUCT_SEARCH',
	(
		keyword,
		filterId,
		page = 1,
		sortBy = 'price',
		sort = 'desc',
		loadCate = false,
		append = false,
	) => async (_, getState) => {
		const { status, data } = await wrapFetch(
			'product/search',
			{ method: 'GET' },
			{
				keyword,
				sortBy,
				sort,
				page,
				take: 8,
				fliter_id: filterId,
			},
		);

		if (status !== 200) {
			return { data: {}, list: [] };
		}

		const {
			product: {
				data: originProductGroup,
				search: { list: originProductList, filters: originSearchFilters },
			},
		} = getState();

		const { list, data: productData } = normalizeProduct(originProductGroup, data.data);

		return {
			data: append ? { ...originProductGroup, ...productData } : productData,
			list: append ? [...originProductList, ...list] : list,
			filters: loadCate ? data.filters : originSearchFilters,
			currentPage: data.current_page,
			totalPage: data.total_page,
			total: data.total,
			perPage: data.per_page,
		};
	},
);

export const getHotProduct = createAction(
	'GET_HOT_PRODUCT',
	(page = 1, append = false) => async (_, getState) => {
		const { status, data } = await wrapFetch('product/hot', { method: 'GET' }, { page });

		if (status !== 200) {
			return { data: {}, list: [] };
		}

		const {
			product: {
				data: originProductGroup,
				hot: { list: hotList },
			},
		} = getState();

		const { list, data: productData } = normalizeProduct(originProductGroup, data.data);
		const resultList = append ? [...hotList, ...list] : list;

		return {
			data: productData,
			list: resultList,
			currentPage: data.current_page,
			totalPage: data.total_page,
			total: data.total,
			perPage: data.per_page,
		};
	},
);

export const getNewProduct = createAction('GET_NEW_PRODUCT', () => async (_, getState) => {
	const { status, data } = await wrapFetch('product/new');

	if (status !== 200) {
		return { data: {}, list: [] };
	}

	const {
		product: { data: originProductGroup },
	} = getState();

	const { list, data: productData } = normalizeProduct(originProductGroup, data);

	return {
		data: productData,
		list,
	};
});

export const getFeatureProduct = createAction('GET_FEATURE_PRODUCT', () => async (_, getState) => {
	const { status, data } = await wrapFetch('product/feature');

	if (status !== 200) {
		return { data: {}, list: [] };
	}

	const {
		product: { data: originProductGroup },
	} = getState();

	const { list, data: productData } = normalizeProduct(originProductGroup, data);

	return {
		data: productData,
		list,
	};
});

export const getCollectProduct = createAction('GET_COLLECT_PRODUCT', () => async (_, getState) => {
	const { status, data } = await wrapAuthFetch('product/collection');

	if (status !== 200) {
		return { data: {}, list: [] };
	}
	const {
		product: { data: originProductGroup },
	} = getState();

	const { list, data: productData } = normalizeProduct(originProductGroup, data);

	return {
		data: productData,
		list,
	};
});

export const cleanCollectProduct = createAction('CLEAN_COLLECT_PRODUCT');

const updateCollectProduct = createAction(
	'UPDATE_COLLECT_PRODUCT',
	id => async (dispatch, getState) => {
		const { status } = await wrapAuthFetch('product/collection', {
			method: 'POST',
			body: JSON.stringify({ id }),
		});

		if (status !== 200 && status !== 201) {
			return { list: [] };
		}
		const {
			product: {
				collection: { list: originCollect },
			},
		} = getState();

		let resultCollect = [];

		if (originCollect.includes(id)) {
			resultCollect = originCollect.filter(collectId => collectId !== id);
			dispatch(openNormalAlert('商品已從 收藏清單 中移除'));
		} else {
			resultCollect = [...originCollect, id];
			dispatch(openNormalAlert('商品已加入 收藏清單'));
		}

		// TODO: content_name, content_category, contents, currency, value
		sendAddToWishlist({ content_ids: id, content_type: 'product' });

		return { list: resultCollect };
	},
);

export const updateProduct = createAction('UPDATE_PRODUCT', (id, data) => (_, getState) => {
	const {
		product: { data: productData },
	} = getState();

	const resultProductData = checkUpdateProductData(productData, data);

	return { id, data: resultProductData };
});

export const isProductCanUseBonus = product =>
	product.is_discountable === 'ACTIVE' &&
	product.discount_rule &&
	product.discount_rule.real_status === 'ONLINE';

export const getProductById = createAction('GET_PRODUCT_BY_ID', id => async dispatch => {
	const { status, data } = await wrapFetch(`product/show/${id}`);

	if (status !== 200) {
		dispatch(openWarningAlert('商品已下架'));
		dispatch(replace('/product'));
		return { id: -1, data: defaultProductData };
	}

	data.recommends = data.recommends.map(recommend => {
		dispatch(updateProduct(recommend.id, recommend));

		return recommend.id;
	});

	const canUseBonus = isProductCanUseBonus(data);

	return {
		id,
		data: { ...data, canUseBonus },
	};
});

const reducer = {
	product: handleActions(
		{
			GET_PRODUCT_LIST_PENDING: state => ({
				...state,
				products: {
					...state.products,
					loading: true,
				},
			}),

			GET_PRODUCT_LIST_FULFILLED: (state, action) => ({
				...state,
				data: { ...state.data, ...action.payload.data },
				products: {
					...state.products,
					list: action.payload.list,
					loading: false,
					currentPage: action.payload.currentPage,
					totalPage: action.payload.totalPage,
					total: action.payload.total,
					perPage: action.payload.perPage,
				},
			}),

			GET_PRODUCT_SEARCH_PENDING: state => ({
				...state,
				search: {
					...state.search,
					loading: true,
				},
			}),

			GET_PRODUCT_SEARCH_FULFILLED: (state, action) => ({
				...state,
				data: { ...state.data, ...action.payload.data },
				search: {
					...state.search,
					list: action.payload.list,
					filters: action.payload.filters,
					loading: false,
					currentPage: action.payload.currentPage,
					totalPage: action.payload.totalPage,
					total: action.payload.total,
					perPage: action.payload.perPage,
				},
			}),

			GET_HOT_PRODUCT_PENDING: state => ({
				...state,
				hot: {
					...state.hot,
					loading: true,
				},
			}),

			GET_HOT_PRODUCT_FULFILLED: (state, action) => ({
				...state,
				data: { ...state.data, ...action.payload.data },
				hot: {
					...state.hot,
					list: action.payload.list,
					loading: false,
					currentPage: action.payload.currentPage,
					totalPage: action.payload.totalPage,
					total: action.payload.total,
					perPage: action.payload.perPage,
				},
			}),

			GET_NEW_PRODUCT_PENDING: state => ({
				...state,
				newProduct: {
					...state.newProduct,
					loading: true,
				},
			}),

			GET_NEW_PRODUCT_FULFILLED: (state, action) => ({
				...state,
				data: { ...state.data, ...action.payload.data },
				newProduct: {
					...state.newProduct,
					list: action.payload.list,
					loading: false,
				},
			}),

			GET_FEATURE_PRODUCT_PENDING: state => ({
				...state,
				feature: {
					...state.feature,
					loading: true,
				},
			}),

			GET_FEATURE_PRODUCT_FULFILLED: (state, action) => ({
				...state,
				data: { ...state.data, ...action.payload.data },
				feature: {
					...state.feature,
					list: action.payload.list,
					loading: false,
				},
			}),

			GET_COLLECT_PRODUCT_PENDING: state => ({
				...state,
				collection: {
					...state.collection,
					loading: true,
				},
			}),

			GET_COLLECT_PRODUCT_FULFILLED: (state, action) => ({
				...state,
				data: { ...state.data, ...action.payload.data },
				collection: {
					...state.collection,
					list: action.payload.list,
					loading: false,
				},
			}),

			CLEAN_COLLECT_PRODUCT: state => ({
				...state,
				collection: {
					list: [],
					loading: false,
				},
			}),

			UPDATE_COLLECT_PRODUCT_FULFILLED: (state, action) => ({
				...state,
				collection: {
					...state.collection,
					list: action.payload.list,
				},
			}),

			GET_PRODUCT_BY_ID_PENDING: state => ({
				...state,
				byId: {
					loading: true,
				},
			}),
			GET_PRODUCT_BY_ID_FULFILLED: (state, action) => ({
				...state,
				data: {
					...state.data,
					[action.payload.id]: action.payload.data,
				},
				byId: {
					loading: false,
				},
			}),

			UPDATE_PRODUCT: (state, action) => ({
				...state,
				data: {
					...state.data,
					[action.payload.id]: action.payload.data,
				},
			}),

			UPDATE_NAME_AND_QUANTITY: (state, action) => ({
				...state,
				productStatus: {
					...state.productStatus,
					value: action.payload,
				},
			}),

			LOAD_CART_DATA_FROM_LOCALSTORAGE: (state, action) => ({
				...state,

				data: { ...state.data, ...action.payload.productData },
			}),
		},
		{
			data: {},
			byId: {
				loading: false,
			},
			products: {
				list: [],
				loading: false,
				currentPage: 0,
				totalPage: 0,
				total: 0,
				perPage: 0,
			},
			search: {
				list: [],
				filters: [],
				loading: false,
				currentPage: 0,
				totalPage: 0,
				total: 0,
				perPage: 0,
			},
			hot: {
				list: [],
				loading: false,
				currentPage: 0,
				totalPage: 0,
				total: 0,
				perPage: 0,
			},
			newProduct: {
				list: [],
				loading: false,
			},
			feature: {
				list: [],
				loading: false,
			},
			collection: {
				list: [],
				loading: false,
			},
		},
	),
};

const mapHooksToState = createSelector(
	state => state.product.products,
	products => ({ product: products }),
);
export const useProductList = () => useRedux(mapHooksToState, { getProduct: getProductList });

const mapHooksToStateBySearch = createSelector(
	state => state.product.search,
	search => ({ product: search }),
);
export const useProductSearch = () =>
	useRedux(mapHooksToStateBySearch, { getProduct: getProductSearch });

const mapHooksToStateByHot = createSelector(
	state => state.product.hot,
	hot => ({ product: hot }),
);
export const useHotProduct = () => useRedux(mapHooksToStateByHot, { getHotProduct });

const mapHooksToStateByNew = createSelector(
	state => state.product.newProduct,
	newProduct => ({ product: newProduct }),
);
export const useNewProduct = () => useRedux(mapHooksToStateByNew, { getNewProduct });

const mapHooksToStateByFeature = createSelector(
	state => state.product.feature,
	feature => ({ product: feature }),
);
export const useFeatureProduct = () => useRedux(mapHooksToStateByFeature, { getFeatureProduct });

const mapHooksToStateByCollect = createSelector(
	state => state.product.collection,
	state => state.product.data,
	(collection, productData) => ({ product: collection, productData }),
);
export const useCollectProduct = () =>
	useRedux(mapHooksToStateByCollect, {
		getCollectProduct,
		updateCollectProduct,
		cleanCollectProduct,
	});

const productCardSelector = createSelector(
	(state, id) => state.product.data[id] || defaultProductData,
	product => ({
		type: product.categories[0].name,
		title: product.name,
		price: product.price,
		discount: product.discount,
		cover: product.cover,
		featureCover: product.feature_cover,
		description: product.description,
	}),
);

export const useProductCard = id => useRedux(state => productCardSelector(state, id));

const productSelector = createSelector(
	(state, id) => state.product.data[id] || defaultProductData,
	product => ({
		type: product.categories[0].name,
		title: product.name,
		price: product.price,
		discount: product.discount,
		cover: product.cover,
		featureCover: product.feature_cover,
		description: product.description,
		breadcrumb: product.bread_crumb,
		features: product.features,
		stocks: product.stocks,
		specs: product.specs,
		deliveries: product.deliveries,
		items: product.items,
		recommends: product.recommends,
		content: product.content,
		additional: product.increase_purchases,
		isDiscountable: product.is_discountable,
		maxDiscountPercentage: product.max_discount_percentage,
		discountRule: product.discount_rule,
		canUseBonus: product.canUseBonus,
		isCreditable: product.is_creditable,
	}),
);

export const useProductById = id => useRedux(state => productSelector(state, id));

export default { reducer };
